/**
 * Trail Configuration Management Component
 * 
 * Manages the Time Trials tab content on the Configuration page
 * - Displays list of trail configurations in card grid format
 * - Create/Edit/Delete trail configurations
 * - Modal forms for CRUD operations
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 5.1, 5.2, 5.3, 6.1, 7.1, 8.1
 */

import { useCallback, useEffect, useState } from 'react';
import { restClient, TrailConfiguration } from '../../../api/rest-client';
import { Card } from '../../common/Card';
import { Button } from '../../common/Button';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { Modal } from '../../common/Modal';
import { useUIStore } from '../../../store/uiStore';
import { TrailConfigurationForm } from './TrailConfigurationForm';

/**
 * Delete Confirmation Modal
 */
interface DeleteConfirmationProps {
  item: TrailConfiguration | null;
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function DeleteConfirmation({ item, isOpen, onConfirm, onCancel, isLoading }: DeleteConfirmationProps) {
  if (!item) return null;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Delete Trail Configuration">
      <div className="space-y-4">
        <p className="text-gray-700">
          Are you sure you want to delete <strong>{item.trail_name} ({item.direction})</strong>?
        </p>
        <p className="text-sm text-red-600">
          This action cannot be undone.
        </p>
        <div className="flex gap-3 pt-4">
          <Button variant="danger" onClick={onConfirm} disabled={isLoading} className="flex-1">
            {isLoading ? 'Deleting...' : 'Delete Trail Configuration'}
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Trail Configuration Management Component
 */
export function TrailConfigurationManagement() {
  const showToast = useUIStore(state => state.showToast);
  const [trailConfigurations, setTrailConfigurations] = useState<TrailConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTrail, setSelectedTrail] = useState<TrailConfiguration | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Fetch all trail configurations
   */
  const fetchTrailConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await restClient.getTrailConfigurations();
      
      // Sort by trail_name then direction
      const sorted = [...data].sort((a, b) => {
        const nameCompare = a.trail_name.localeCompare(b.trail_name);
        if (nameCompare !== 0) return nameCompare;
        return a.direction.localeCompare(b.direction);
      });
      
      setTrailConfigurations(sorted);
    } catch (err) {
      console.error('Failed to fetch trail configurations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load trail configurations');
      showToast('error', 'Failed to load trail configurations');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  /**
   * Handle create trail configuration
   */
  const handleCreate = useCallback(() => {
    setSelectedTrail(null);
    setIsFormModalOpen(true);
  }, []);

  /**
   * Handle edit trail configuration
   */
  const handleEdit = useCallback((trail: TrailConfiguration) => {
    setSelectedTrail(trail);
    setIsFormModalOpen(true);
  }, []);

  /**
   * Handle delete trail configuration
   */
  const handleDelete = useCallback((trail: TrailConfiguration) => {
    setSelectedTrail(trail);
    setIsDeleteModalOpen(true);
  }, []);

  /**
   * Submit form (create or update)
   */
  const handleSubmit = useCallback(async (data: Omit<TrailConfiguration, 'trail_id' | 'is_active' | 'created_at' | 'updated_at'>) => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (selectedTrail) {
        // Update existing trail configuration
        await restClient.updateTrailConfiguration(selectedTrail.trail_id, data);
        showToast('success', 'Trail configuration updated successfully');
      } else {
        // Create new trail configuration
        await restClient.createTrailConfiguration(data);
        showToast('success', 'Trail configuration created successfully');
      }

      await fetchTrailConfigurations();
      setIsFormModalOpen(false);
      setSelectedTrail(null);
    } catch (err) {
      console.error('Failed to save trail configuration:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save trail configuration';
      setError(errorMessage);
      showToast('error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedTrail, fetchTrailConfigurations, showToast]);

  /**
   * Confirm delete
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!selectedTrail) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await restClient.deleteTrailConfiguration(selectedTrail.trail_id);
      showToast('success', 'Trail configuration deleted successfully');
      
      await fetchTrailConfigurations();
      setIsDeleteModalOpen(false);
      setSelectedTrail(null);
    } catch (err) {
      console.error('Failed to delete trail configuration:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete trail configuration';
      setError(errorMessage);
      showToast('error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedTrail, fetchTrailConfigurations, showToast]);

  // Fetch data on mount
  useEffect(() => {
    fetchTrailConfigurations();
  }, [fetchTrailConfigurations]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Trail Configurations</h2>
        <Button variant="primary" onClick={handleCreate}>
          Create Trail Configuration
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Trail Configurations List */}
      {trailConfigurations.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-900">No trail configurations found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new trail configuration
            </p>
            <div className="mt-6">
              <Button variant="primary" onClick={handleCreate}>
                Create Trail Configuration
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trailConfigurations.map((trail) => (
            <Card key={trail.trail_id} className="hover:shadow-lg transition-shadow">
              <div className="flex flex-col h-full">
                {/* Trail Info */}
                <div className="flex-grow mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{trail.trail_name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{trail.direction}</p>
                  {trail.is_default && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      Default
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-auto">
                  <Button variant="secondary" size="small" onClick={() => handleEdit(trail)} className="flex-1">
                    Edit
                  </Button>
                  <Button variant="danger" size="small" onClick={() => handleDelete(trail)}>
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Form Modal */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedTrail(null);
        }}
        title={selectedTrail ? 'Edit Trail Configuration' : 'Create Trail Configuration'}
      >
        <TrailConfigurationForm
          trailConfiguration={selectedTrail || undefined}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsFormModalOpen(false);
            setSelectedTrail(null);
          }}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        item={selectedTrail}
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setSelectedTrail(null);
        }}
        isLoading={isSubmitting}
      />
    </>
  );
}
